<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div class="flex flex-col py-3 px-2 mx-4 space-y-3">
      <div class="w-full flex space-x-2">
        <div class="w-1/2">
          <label
            :for="`carrier_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Carrier:
          </label>
          <select
            v-model="carrier"
            class="mt-1 form-select block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option v-for="shippingMethod of shippingMethods" :key="shippingMethod">{{ shippingMethod }}</option>
          </select>
        </div>
        <div class="w-1/2">
          <label
            :for="`tracking_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Tracking #:
          </label>
          <input
            v-model="trackingNumber"
            type="number"
            :id="`tracking_${_uid}`"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
        </div>
      </div>
      <div class="w-full flex space-x-2">
        <div class="w-1/2">
          <label
            :for="`pickup_date_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Pickup Date:
          </label>
          <v-date-picker
            class="block mt-1"
            color="green"
            v-model="pickupDate"
          />
        </div>
        <div class="w-1/2">
          <label
            :for="`cost_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Cost (optional):
          </label>
          <input
            :value="cost"
            @change="cost = null; cost = decimal($event.target.value)"
            type="number"
            step="0.01"
            :id="`cost_${_uid}`"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
        </div>
      </div>
      <div class="w-full flex space-x-2">
        <div class="w-1/2">
          <label
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Facility:
          </label>
          <select
            :id="`facility_${_uid}`"
            v-model="selectedFaciltyId"
            class="mt-1 form-select block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          >
            <option
              v-for="facility in facilities"
              :key="facility.id"
              :value="facility.id"
            >
              {{ facility.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <HttpError :error="error" dismissable />

    <!-- Footer -->
    <div class="flex justify-end px-4 py-4 space-x-4">
      <span class="inline-flex rounded-md shadow-sm">
        <button
          type="button"
          @click="$emit('toggle-slideover')"
          class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
        >
          Cancel
        </button>
      </span>
      <span class="inline-flex rounded-md shadow-sm">
        <Button
          @click-handler="process"
          :disabled="! canProcess || processing"
          class="flex items-center space-x-2"
        >
          <span>Add Tracking</span>
          <LoadingSpinner v-if="processing" class="text-white" />
        </Button>
      </span>
    </div>

  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import dateFormatter from "@/mixins/dateFormatter.js";
import Slideover from "@/components/layout/Slideover.vue";

const Order = ApiFactory.get('orders');
const FacilitiesAPI = ApiFactory.get("facilities");

export default {
  name: "AddTracking",
  components: {
    Button,
    Slideover,
  },
  filters: {
    decimals(value) {
      return Number(value).toFixed(2);
    }
  },
  props: {
    title: {
      type: String,
      default: "Title"
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      carrier: 'FedEx',
      trackingNumber: null,
      pickupDate: null,
      cost: null,
      error: null,
      processing: false,
      shippingMethods: ['PU', 'FRGHT', 'FedEx', 'ONTRAC', 'UPS', 'USPS'],
      facilities: [],
      selectedFaciltyId: null
    }
  },
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    canProcess() {
      if (! this.carrier) {
        return false;
      }

      if (! this.trackingNumber) {
        return false;
      }

      if (!this.pickupDate) {
        return false;
      }

      if (!this.selectedFaciltyId) {
        return false;
      }

      return true;
    }
  },
  async created() {
    await FacilitiesAPI.getFacilities({ type_id: 1 }).then(response => {
      this.facilities = response.data.body;
      this.selectedFaciltyId = this.facilities[0].id;
    });
  },
  methods: {
    decimal(value, digits) {
      if (digits == undefined) {
        digits = 2;
      }

      return Number(value).toFixed(digits);
    },
    process() {
      this.processing = true;

      const body = {
        carrier: this.carrier,
        tracking_id: Number(this.trackingNumber),
        pickup_date: dateFormatter.toDateString(this.pickupDate), // TODO may need to adjust
        facility_id: this.selectedFaciltyId,
      };

      if (this.cost != null && this.cost != '') {
        body['cost'] = Number(this.cost);
      }

      Order.addTracking(
        this.order.OrderNumber,
        body
      )
        .then(this.handleSuccess)
        .catch((error) => this.error = error)
        .finally(() => this.processing = false);
    },
    handleSuccess() {
      this.$emit('toggle-slideover');
      this.$emit('refresh-order');
    }
  }
};
</script>
